/**
 * feeTree/feeConfigUtils.ts
 * Utility functions for fee configuration mutations
 *
 * Provides helpers for:
 * - Finding configs by category path and/or instrumentId
 * - Building FeeTree node IDs from configs (for selection sync)
 * - Building FeeConfigPath from FeeTreeNodes (reverse of node ID)
 * - Building display names for configs
 * - Creating default fee configurations
 *
 * These utilities are used by BrokersContext mutation methods.
 */

import type { BrokerFeeConfiguration } from '@/types/schema/Broker';
import type {
  AssetClass,
  AssetClassType,
  MarketSegment,
  SubAssetClass,
} from '@/types/shared/AssetClass';
import type { DisplayMaps, FeeConfigPath, FeeTreeNode } from './types';
import { generateUUID } from '@/utils/uuid';

// =============================================================================
// Types
// =============================================================================

/**
 * Parameters for creating a default fee configuration
 */
export interface CreateDefaultFeeConfigParams {
  /** Category path (undefined for uncategorized instruments) */
  category?: AssetClass;
  /** Instrument ID (undefined for category-level configs) */
  instrumentId?: string;
  /** Display maps for building config name */
  displayMaps: DisplayMaps;
}

// =============================================================================
// Find Config by Path
// =============================================================================

/**
 * Find a fee configuration by category path and/or instrumentId
 *
 * Checks BOTH active and archived configs - caller decides action based on `status`.
 * Uses exact match on category fields (assetClass, marketSegment, subAssetClass).
 *
 * @param configs - Array of BrokerFeeConfiguration to search
 * @param category - Category to match (undefined to match configs with no category)
 * @param instrumentId - Instrument ID to match (undefined for category-level configs)
 * @returns Matching config or undefined if not found
 */
export function findConfigByPath(
  configs: BrokerFeeConfiguration[],
  category?: AssetClass,
  instrumentId?: string
): BrokerFeeConfiguration | undefined {
  return configs.find((config) => {
    // Match instrumentId (both undefined means category-level)
    if (config.instrumentId !== instrumentId) {
      return false;
    }

    // Match category
    if (!category && !config.category) {
      // Both undefined - match (uncategorized or root-level)
      return true;
    }

    if (!category || !config.category) {
      // One has category, other doesn't - no match
      return false;
    }

    // Both have category - exact match on all fields
    return (
      config.category.assetClass === category.assetClass &&
      config.category.marketSegment === category.marketSegment &&
      config.category.subAssetClass === category.subAssetClass
    );
  });
}

// =============================================================================
// Build Node ID from Config
// =============================================================================

/**
 * Generate FeeTree node ID from a BrokerFeeConfiguration
 *
 * Mirrors the node ID scheme from buildFeeTree.ts for consistency:
 * - Category nodes: `category:{assetClass}/{marketSegment}/{subAssetClass?}`
 * - Instrument nodes: `category:{path}/instrument:{instrumentId}`
 * - Uncategorized instruments: `category:unknown/instrument:{instrumentId}`
 *
 * @param config - The fee configuration
 * @returns Stable node ID for FeeTree selection
 */
export function buildNodeIdFromConfig(config: BrokerFeeConfiguration): string {
  const { category, instrumentId } = config;

  if (instrumentId) {
    // Instrument node
    if (category) {
      // Categorized instrument: category:{path}/instrument:{id}
      const path = buildCategoryPath(category);
      return `category:${path}/instrument:${instrumentId}`;
    } else {
      // Uncategorized instrument: category:unknown/instrument:{id}
      return `category:unknown/instrument:${instrumentId}`;
    }
  } else if (category) {
    // Category-level config
    const path = buildCategoryPath(category);
    return `category:${path}`;
  }

  // Neither category nor instrumentId - shouldn't happen with valid configs
  // Return empty string to signal invalid config
  return '';
}

/**
 * Build category path string from AssetClass
 * @internal
 */
function buildCategoryPath(category: AssetClass): string {
  const parts: string[] = [category.assetClass, category.marketSegment];
  if (category.subAssetClass) {
    parts.push(category.subAssetClass);
  }
  return parts.join('/');
}

// =============================================================================
// Build FeeConfigPath from FeeTreeNode
// =============================================================================

/**
 * Build a FeeConfigPath from a FeeTreeNode
 *
 * Reverse of buildNodeIdFromConfig: given a tree node, derive the FeeConfigPath
 * that would be used for fee configuration operations at that node.
 *
 * Handles all node types:
 * - Instrument with category: `{ category, instrumentId: node.value }`
 * - Instrument uncategorized (categoryPath = []): `{ instrumentId: node.value }`
 * - Category (market-segment, sub-asset-class): `{ category }`
 * - Asset-class level or invalid: `null` (no valid fee config at this level)
 *
 * @param node - The FeeTreeNode to derive path from
 * @returns FeeConfigPath for operations, or null if node can't map to a valid config path
 */
export function buildFeeConfigPath(node: FeeTreeNode): FeeConfigPath | null {
  if (node.type === 'instrument') {
    // Uncategorized instruments have categoryPath = [] in final FeeTreeNode
    if (node.categoryPath.length < 2) {
      return { instrumentId: node.value };
    }
    const [ac, ms, sac] = node.categoryPath;
    const category: AssetClass = {
      assetClass: ac as AssetClassType,
      marketSegment: ms as MarketSegment,
      ...(sac && { subAssetClass: sac as SubAssetClass }),
    };
    return { category, instrumentId: node.value };
  }

  // Category nodes need at least assetClass + marketSegment
  if (node.categoryPath.length < 2) {
    // Asset-class level — no valid fee config at this level
    return null;
  }

  const [ac, ms, sac] = node.categoryPath;
  const category: AssetClass = {
    assetClass: ac as AssetClassType,
    marketSegment: ms as MarketSegment,
    ...(sac && { subAssetClass: sac as SubAssetClass }),
  };
  return { category };
}

// =============================================================================
// Build Config Name
// =============================================================================

/**
 * Build display name for a fee configuration from its path
 *
 * - Category configs: "Futures / Indices / Micro" (joined with " / ")
 * - Instrument configs: uses instrumentId as name (e.g., "MNQ")
 *
 * TODO: Enhance to use instrument display name from lookup
 *
 * @param category - Category to derive name from
 * @param instrumentId - Instrument ID (takes precedence if provided)
 * @param displayMaps - Display maps for translating category values to labels
 * @returns Display name for the configuration
 */
export function buildConfigName(
  category: AssetClass | undefined,
  instrumentId: string | undefined,
  displayMaps: DisplayMaps
): string {
  // Instrument configs use instrumentId as name
  if (instrumentId) {
    return instrumentId;
  }

  // Category configs build name from path
  if (category) {
    const parts: string[] = [];

    // Asset class type
    const assetClassLabel =
      displayMaps.assetClassType[category.assetClass] ?? category.assetClass;
    parts.push(assetClassLabel);

    // Market segment
    const marketSegmentLabel =
      displayMaps.marketSegment[category.marketSegment] ?? category.marketSegment;
    parts.push(marketSegmentLabel);

    // Sub-asset class (optional)
    if (category.subAssetClass) {
      const subAssetClassLabel =
        displayMaps.subAssetClass[category.subAssetClass] ?? category.subAssetClass;
      parts.push(subAssetClassLabel);
    }

    return parts.join(' / ');
  }

  // No category or instrumentId - default name
  return 'Unnamed Configuration';
}

// =============================================================================
// Find Node by ID
// =============================================================================

/**
 * Find a FeeTreeNode by its ID in the tree hierarchy
 *
 * Performs a depth-first search through the tree and all descendants.
 * Used by FeeConfigurationEditor to resolve selectedNodeId into node metadata.
 *
 * @param tree - Root-level FeeTreeNode array from buildFeeTree
 * @param nodeId - The node ID to find (e.g., "category:futures/indices/micro")
 * @returns The matching FeeTreeNode, or null if not found
 */
export function findNodeById(
  tree: FeeTreeNode[],
  nodeId: string
): FeeTreeNode | null {
  for (const node of tree) {
    if (node.id === nodeId) {
      return node;
    }
    const found = findNodeById(node.children, nodeId);
    if (found) {
      return found;
    }
  }
  return null;
}

// =============================================================================
// Find Inherited Config (Nearest Ancestor)
// =============================================================================

/**
 * Find the nearest ancestor fee configuration for a node that has no direct config.
 *
 * Walks up the category hierarchy from the node's level toward the root, checking
 * for an active `BrokerFeeConfiguration` at each ancestor level. Useful for both
 * inherited-mode display (showing what the node inherits) and edit-mode
 * "Inherit from Parent" (showing what the node would revert to).
 *
 * Walk-up strategy:
 * - Instrument nodes: start at the parent category level (same categoryPath, no instrumentId)
 * - Category nodes: start one level above (trimmed categoryPath)
 * - Skip 1-segment paths (asset-class level) since configs can't exist there
 *
 * @param node - The FeeTreeNode to find an ancestor for
 * @param configs - All BrokerFeeConfigurations for the broker
 * @returns The nearest ancestor's active config, or null if none found
 */
export function findInheritedConfig(
  node: FeeTreeNode,
  configs: BrokerFeeConfiguration[]
): BrokerFeeConfiguration | null {
  const { categoryPath, type } = node;

  // No ancestors to search for nodes without a category path
  if (categoryPath.length < 2) {
    return null;
  }

  // Determine the starting depth for ancestor search
  // Instrument nodes: start at full categoryPath (parent category level)
  // Category nodes: start one level above (trim last segment)
  const startLength = type === 'instrument' ? categoryPath.length : categoryPath.length - 1;

  // Walk up from startLength down to 2 (minimum valid config depth)
  for (let len = startLength; len >= 2; len--) {
    const ancestorPath = categoryPath.slice(0, len);
    const [ac, ms, sac] = ancestorPath;

    const category: AssetClass = {
      assetClass: ac as AssetClassType,
      marketSegment: ms as MarketSegment,
      ...(sac && { subAssetClass: sac as SubAssetClass }),
    };

    const config = findConfigByPath(configs, category);
    if (config && config.status === 'active') {
      return config;
    }
  }

  return null;
}

// =============================================================================
// Create Default Fee Config
// =============================================================================

/**
 * Create a new BrokerFeeConfiguration with default values
 *
 * Sets required fields with sensible defaults:
 * - `id`: New UUID
 * - `name`: Derived from category/instrumentId via buildConfigName
 * - `status`: 'active'
 * - `feesUpdatedAt`: Current ISO timestamp
 * - `commission`: Minimal valid structure (spreadOnly)
 *
 * @param params - Configuration parameters
 * @returns New BrokerFeeConfiguration ready for insertion
 */
export function createDefaultFeeConfig(
  params: CreateDefaultFeeConfigParams
): BrokerFeeConfiguration {
  const { category, instrumentId, displayMaps } = params;

  const config: BrokerFeeConfiguration = {
    id: generateUUID(),
    name: buildConfigName(category, instrumentId, displayMaps),
    status: 'active',
    feesUpdatedAt: new Date().toISOString(),
    commission: {
      feeType: 'spreadOnly',
      chargeOn: 'perSide',
      verificationStatus: 'userOverride',
    },
  };

  // Add optional fields only if provided
  if (category) {
    config.category = category;
  }
  if (instrumentId) {
    config.instrumentId = instrumentId;
  }

  return config;
}
